import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  ArrowLeft, Plus, Trash2, Bell, TrendingUp, TrendingDown,
  Star, Search, ExternalLink, RefreshCw
} from 'lucide-react';
import { Button } from '../components/ui/button';
import UpgradeModal from '../components/UpgradeModal';

const API_BASE = process.env.REACT_APP_BACKEND_URL + '/api';

const WatchlistPage = () => {
  const navigate = useNavigate();
  const { user, isAuthenticated, isPro, token } = useAuth();
  
  const [watchlist, setWatchlist] = useState([]);
  const [loading, setLoading] = useState(true);
  const [addSymbol, setAddSymbol] = useState('');
  const [adding, setAdding] = useState(false);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [stockData, setStockData] = useState({});
  
  // Redirect free users and show upgrade modal
  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
    if (!isPro) {
      setShowUpgradeModal(true);
    }
  }, [isAuthenticated, isPro, navigate]);
  
  // Fetch watchlist from API
  const fetchWatchlist = useCallback(async () => {
    if (!isPro) return;
    
    setLoading(true);
    try {
      const response = await axios.get(`${API_BASE}/watchlist`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setWatchlist(response.data.symbols || []);
    } catch (error) {
      if (error.response?.status === 403) {
        setShowUpgradeModal(true);
      } else {
        console.error('Error fetching watchlist:', error);
        toast.error('Failed to load watchlist');
      }
    } finally {
      setLoading(false);
    }
  }, [isPro, token]);
  
  useEffect(() => {
    fetchWatchlist();
  }, [fetchWatchlist]);
  
  // Fetch stock data for watchlist symbols
  const fetchStockData = useCallback(async () => {
    if (watchlist.length === 0) return;
    
    try {
      const response = await axios.get(`${API_BASE}/anomalies?time_range=ALL`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      
      const anomalies = response.data.anomalies || [];
      const dataMap = {};
      
      for (const anomaly of anomalies) {
        dataMap[anomaly.symbol] = {
          dailyReturn: anomaly.daily_return,
          volumeMultiple: anomaly.volume_multiple,
          score: anomaly.unusual_activity_score,
          date: anomaly.date
        };
      }
      
      setStockData(dataMap);
    } catch (error) {
      console.error('Error fetching stock data:', error);
    }
  }, [watchlist, token]);
  
  useEffect(() => {
    fetchStockData();
  }, [fetchStockData]);
  
  // Add symbol to watchlist
  const handleAddSymbol = async (e) => {
    e.preventDefault();
    if (!addSymbol.trim()) return;
    
    const symbol = addSymbol.trim().toUpperCase();
    
    if (watchlist.includes(symbol)) {
      toast.error(`${symbol} is already in your watchlist`);
      return;
    }
    
    setAdding(true);
    try {
      const response = await axios.post(`${API_BASE}/watchlist/add`, 
        { symbol },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setWatchlist(response.data.symbols || []);
      setAddSymbol('');
      toast.success(`Added ${symbol} to watchlist`);
    } catch (error) {
      if (error.response?.status === 403) {
        setShowUpgradeModal(true);
      } else {
        toast.error('Failed to add symbol');
      }
    } finally {
      setAdding(false);
    }
  };
  
  // Remove symbol from watchlist
  const handleRemoveSymbol = async (symbol) => {
    try {
      const response = await axios.post(`${API_BASE}/watchlist/remove`,
        { symbol },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setWatchlist(response.data.symbols || []);
      toast.success(`Removed ${symbol} from watchlist`);
    } catch (error) {
      toast.error('Failed to remove symbol');
    }
  };
  
  // Handle upgrade modal close
  const handleUpgradeModalClose = () => {
    setShowUpgradeModal(false);
    if (!isPro) {
      navigate('/app');
    }
  };
  
  if (!isPro && showUpgradeModal) {
    return (
      <UpgradeModal 
        isOpen={showUpgradeModal} 
        onClose={handleUpgradeModalClose}
        feature="Watchlists"
      />
    );
  }
  
  return (
    <div className="min-h-screen" style={{ backgroundColor: '#09090b' }}>
      {/* Header */}
      <div className="border-b" style={{ borderColor: 'rgba(255,255,255,0.06)' }}>
        <div className="max-w-4xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate('/app')}
                className="p-2 rounded-lg transition-colors"
                style={{ color: '#71717a' }}
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
              <div>
                <h1 className="text-xl font-semibold" style={{ color: '#f4f4f5' }}>
                  My Watchlist
                </h1>
                <p className="text-sm" style={{ color: '#71717a' }}>
                  {watchlist.length} symbol{watchlist.length !== 1 ? 's' : ''} tracked
                </p>
              </div>
            </div>
            
            <Button
              variant="outline"
              size="sm"
              onClick={fetchWatchlist}
              disabled={loading}
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>
        </div>
      </div>
      
      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 py-6">
        {/* Add Symbol Form */}
        <form onSubmit={handleAddSymbol} className="mb-6">
          <div className="flex gap-3">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: '#52525b' }} />
              <input
                type="text"
                value={addSymbol}
                onChange={(e) => setAddSymbol(e.target.value.toUpperCase())}
                placeholder="Add symbol (e.g., AAPL)"
                className="w-full pl-10 pr-4 py-2.5 rounded-lg text-sm"
                style={{
                  backgroundColor: 'rgba(255,255,255,0.03)',
                  border: '1px solid rgba(255,255,255,0.08)',
                  color: '#f4f4f5'
                }}
                maxLength={10}
              />
            </div>
            <Button type="submit" disabled={adding || !addSymbol.trim()}>
              <Plus className="w-4 h-4 mr-2" />
              Add
            </Button>
          </div>
        </form>
        
        {/* Watchlist Items */}
        {loading ? (
          <div className="text-center py-12" style={{ color: '#71717a' }}>
            Loading watchlist...
          </div>
        ) : watchlist.length === 0 ? (
          <div 
            className="text-center py-12 rounded-xl"
            style={{ 
              backgroundColor: 'rgba(255,255,255,0.02)',
              border: '1px dashed rgba(255,255,255,0.1)'
            }}
          >
            <Star className="w-12 h-12 mx-auto mb-4" style={{ color: '#3f3f46' }} />
            <h3 className="text-lg font-medium mb-2" style={{ color: '#a1a1aa' }}>
              Your watchlist is empty
            </h3>
            <p style={{ color: '#71717a' }}>
              Add symbols above to start tracking stocks
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {watchlist.map((symbol) => {
              const data = stockData[symbol];
              const isPositive = data?.dailyReturn >= 0;
              
              return (
                <div
                  key={symbol}
                  className="p-4 rounded-xl flex items-center justify-between"
                  style={{
                    backgroundColor: 'rgba(255,255,255,0.02)',
                    border: '1px solid rgba(255,255,255,0.06)'
                  }}
                >
                  <div className="flex items-center gap-4">
                    <div 
                      className="w-10 h-10 rounded-lg flex items-center justify-center font-medium text-sm"
                      style={{
                        backgroundColor: data ? (isPositive ? 'rgba(74,222,128,0.1)' : 'rgba(248,113,113,0.1)') : 'rgba(255,255,255,0.05)',
                        color: data ? (isPositive ? '#4ade80' : '#f87171') : '#71717a'
                      }}
                    >
                      {symbol.slice(0, 2)}
                    </div>
                    <div>
                      <button
                        onClick={() => navigate(`/app/stock/${symbol}`)}
                        className="font-medium text-left hover:underline"
                        style={{ color: '#f4f4f5' }}
                      >
                        {symbol}
                      </button>
                      {data && (
                        <div className="flex items-center gap-2 mt-0.5">
                          {isPositive ? (
                            <TrendingUp className="w-3 h-3" style={{ color: '#4ade80' }} />
                          ) : (
                            <TrendingDown className="w-3 h-3" style={{ color: '#f87171' }} />
                          )}
                          <span 
                            className="text-sm"
                            style={{ color: isPositive ? '#4ade80' : '#f87171' }}
                          >
                            {isPositive ? '+' : ''}{data.dailyReturn?.toFixed(2)}%
                          </span>
                          {data.score && (
                            <span className="text-xs px-1.5 py-0.5 rounded" style={{ 
                              backgroundColor: 'rgba(124,92,255,0.1)',
                              color: '#a78bfa'
                            }}>
                              Score: {data.score.toFixed(0)}
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => navigate(`/app/alerts?symbol=${symbol}`)}
                      title="Create alert"
                    >
                      <Bell className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => navigate(`/app/stock/${symbol}`)}
                      title="View details"
                    >
                      <ExternalLink className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleRemoveSymbol(symbol)}
                      className="text-red-400 hover:text-red-300"
                      title="Remove from watchlist"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
      
      <UpgradeModal 
        isOpen={showUpgradeModal && isPro === false} 
        onClose={handleUpgradeModalClose}
        feature="Watchlists"
      />
    </div>
  );
};

export default WatchlistPage;
